import type { HidChannel } from '../../../channels/hid/HidChannel'
import { TelinkKeyboardFirmware } from '../../../chips/telink/feature-impls/keyboard/firmware'
import { TelinkKeyboardPairing } from '../../../chips/telink/feature-impls/keyboard/pairing'
import { TelinkKeyboardLighting } from '../../../chips/telink/feature-impls/keyboard/keyboard-lighting'
import { TelinkKeyboardKeys } from '../../../chips/telink/feature-impls/keyboard/keyboard-keys'
import { TelinkKeyboardPerfCfg } from '../../../chips/telink/feature-impls/keyboard/keyboard-performance'
import { TelinkConfig } from '../../../chips/telink/feature-impls/keyboard/config'
import { BaseDevice } from '../../../runtime/BaseDevice'
import type { DeviceManifest, InputReportCapability } from '../../../types/manifest'
import { ReportEvents } from '../../../events/ReportEvents'
import { NoticeEvents } from '../../../events/NoticeEvents'
import { ReportBackfill } from '../../../cache/ReportBackfill'
import type { ReportBackfillRule } from '../../../cache/types'
import { TELINK_KEYBOARD_REPORT_RULES } from '../../../chips/telink/protocols/keyboard-report-rules'
import { telinkDeviceCustom } from '../../../types/config'
import { delay } from '../../../utils/TimeUtils'
import { ConnectionType } from '../../../types/connection'
import { toAbsoluteConfigIndex } from '../../../utils/configSlots'

const WIRED_CONNECTION_TYPE = 0xff
const RECEIVER_CONNECTION_TYPE = 0xa5

/**
 * Telink 键盘及接收器驱动。
 *
 * 此处只装配 Telink 共用能力；接收器身份沿用 manifest 的 PID 顺序约定，避免芯片协议依赖某个产品系列。
 */
export class TelinkKeyboardDevice extends BaseDevice {
  /** 上报数据回填引擎实例,用于 close/disconnect 时调用 stop() */
  private readonly reportBackfill: ReportBackfill

  /** 初始化连接语义并装配所有 Telink 键盘共用 Feature。 */
  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)
    this.connectionType = this.isReceiverProductId(channel.deviceInfo?.productId)
      ? RECEIVER_CONNECTION_TYPE
      : WIRED_CONNECTION_TYPE
    this.features.pairing = new TelinkKeyboardPairing(this)
    this.features.firmware = new TelinkKeyboardFirmware(this)
    this.features.keyboardLighting = new TelinkKeyboardLighting(this)
    this.features.keyboardKeys = new TelinkKeyboardKeys(this)
    this.features.keyboardPerfCfg = new TelinkKeyboardPerfCfg(this)
    this.features.config = new TelinkConfig(this)

    // Phase 4: 启动上报回填,让高频字段(亮度/速度/cfgSel/电量)通过 report 自动进缓存
    // 规则按 deviceUserCustom 强类型编写,注册时收敛为引擎的 unknown payload(运行时即该类型)
    this.reportBackfill = new ReportBackfill(
      this,
      TELINK_KEYBOARD_REPORT_RULES as ReportBackfillRule[],
    )
    this.reportBackfill.start()
  }

  /**
   * 无线链路时（connectionType=0xa5），payload[1]（命令字节）默认加 0x20 透传，
   * 避免接收器把它当作本机命令。
   *
   * 若某些 payload 的字节[1]不是命令而是普通数据，传入 options.skipCmdOffset = true 跳过。
   */
  async sendAndWait(
    outputReportId: number,
    outputData: Uint8Array,
    inputReportId: number,
    validate: (data: DataView) => boolean = () => true,
    options: {
      timeout?: number
      maxRetries?: number
      skipCmdOffset?: boolean
    } = {},
  ): Promise<DataView> {
    if (this.connectionType === 0xa5 && !options.skipCmdOffset && outputData.length >= 2) {
      const modified = new Uint8Array(outputData)
      modified[1] = (outputData[1] + 0x20) & 0xff
      outputData = modified
    }
    return super.sendAndWait(outputReportId, outputData, inputReportId, validate, options)
  }

  protected onConnected(): void {
    console.log(`[TelinkKeyboardDevice] Connected: ${this.manifest.model}`)
    // 初始化读取当前使用的配置
    this.features.config?.getCurrentConfig().then((config) => {
      this.currentConfig = config
    })
  }

  async checkConnect(): Promise<boolean> {
    try {
      const isConnected = this.channel?.isConnected
      if (!isConnected) {
        return false
      }
      await this.features.keyboardPerfCfg?.getReportRate()
      return true
    } catch (_error) {
      return false
    }
  }

  protected onDisconnected(): void {
    this.reportBackfill.stop()
  }

  /** 按 manifest 中第二个 PID 的既有约定判定接收器身份。 */
  private isReceiverProductId(productId: number | undefined): boolean {
    if (typeof productId !== 'number' || !Number.isInteger(productId)) return false

    const productIds = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]
    return productIds.length > 1 && productId === productIds[1]
  }

  protected handleReport(_reportId: number, data: DataView): void {
    const buffer = new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    const type = buffer[0]
    this.dispatchEvent(type, buffer)
  }

  private async dispatchEvent(type: number, buffer: Uint8Array) {
    switch (type) {
      case 0x01:
        // 用户自定义数据
        const userBuffer = buffer.slice(1)

        // 区分V700和A83 Air的协议报告上传
        const reportCapability = this.manifest.capabilities.find(
          (item): item is InputReportCapability => item.type === 'input-report',
        )
        const userData =
          reportCapability?.config.type === 'V700'
            ? (this.parseV700UserCustomData(userBuffer) as telinkDeviceCustom)
            : (this.parseUserCustomData(userBuffer) as telinkDeviceCustom)
        let isReportCfg = false
        const configIndex = toAbsoluteConfigIndex(
          userData.cfgSel,
          this.manifest.capabilities,
          this.configSlotRoles,
        )
        const reportUserData =
          configIndex === userData.cfgSel ? userData : { ...userData, cfgSlot: configIndex }
        this.emit(ReportEvents.KeyUserData, reportUserData)
        const appUserData =
          configIndex === userData.cfgSel ? userData : { ...reportUserData, cfgSel: configIndex }
        // 同步当前配置
        if (this.currentConfig.index !== configIndex) {
          if (this.getConnectionType() === ConnectionType.Wireless) {
            // V700无线连接时，切换配置后需要等待 150ms 才能读取到正确的配置数据
            await delay(150) // 等待配置切换完成
          }
          this.emit(ReportEvents.CfgChanged, appUserData)
          const currentConfig = this.configList.find((config) => config.index === configIndex)
          if (currentConfig) {
            this.currentConfig = currentConfig
          } else {
            const config = this.features.config
            if (config) {
              const configs = await config.getAllConfigs()
              const matchedConfig = configs.find((cfg) => cfg.index === configIndex)
              if (matchedConfig) {
                this.currentConfig = matchedConfig
              }
            }
          }
          this.emit(NoticeEvents.CfgChanged, this.currentConfig)
          isReportCfg = true
        }
        // 电量或充电状态有变化时才抛出事件。
        // batStatus 来自 FUC_WM Bit14~15(0=无效, 1=有效, 2=充电中),与 byte0 低4位的
        // connectType(连接模式)语义不同 —— 切连接模式不应触发 Battery 事件。
        const prevData = this.userCustomData as typeof userData | undefined
        if (
          prevData?.batPercent !== userData.batPercent ||
          prevData?.batStatus !== userData.batStatus
        ) {
          this.emit(ReportEvents.Battery, {
            batPercent: userData.batPercent,
            batStatus: userData.batStatus,
          })
        }
        // 设备灯效/亮度/速度变更时抛出事件
        const blModeChanged = prevData?.blMode !== userData.blMode
        const blLevelChanged = prevData?.blLevel !== userData.blLevel
        const blSpeedChanged = prevData?.blSpeed !== userData.blSpeed

        // 硬件侧按 FN 灯效切换键会改变 blMode，业务层据此同步当前灯效
        if (blModeChanged) {
          this.emit(ReportEvents.EffectChanged, userData.blMode)
        }
        if (blLevelChanged) {
          this.emit(ReportEvents.BrightnessChanged, userData.blLevel)
          if (prevData !== undefined && !isReportCfg) {
            this.emit(NoticeEvents.BrightnessChanged, userData.blLevel)
          }
        }
        if (blSpeedChanged) {
          this.emit(ReportEvents.SpeedChanged, userData.blSpeed)
          if (prevData !== undefined && !isReportCfg) {
            this.emit(NoticeEvents.SpeedChanged, userData.blSpeed)
          }
        }
        // 系统模式仅在上报携带 sysMode、且相对上次快照发生变化时通知 UI。
        // V700 等旧协议解析结果不含该字段，不会误报；首包只建快照，避免把初始化当成切换。
        if (
          userData.sysMode !== undefined &&
          prevData !== undefined &&
          prevData.sysMode !== userData.sysMode
        ) {
          this.emit(ReportEvents.SysModeChanged, userData.sysMode)
        }
        // 更新用户自定义数据
        this.userCustomData = appUserData
        // 灯效/亮度/速度上报时失效当前配置下的灯光缓存。
        // 上报只带 3 个字段，凑不出完整的 8 字节配置块，所以走失效而非回填，
        // 让下次读取从硬件拿到自洽的整块数据。
        if (blModeChanged || blLevelChanged || blSpeedChanged) {
          this.cache.invalidateByTag(`cfg:${this.currentConfig.index}`)
        }
        break
    }
  }

  /**
   * 解析 0x01 用户自定义数据（7 字节，Telink 键盘专用格式）
   * 新的协议上报数据处理
   * 适用的是A83air，R67以及之后的VK系列键盘
   * 字节布局：
   * - Byte 0: cfgSel — 当前配置模式  高 4 位
   *           模式 — 低 4 位 0x02:usb 0x01:ble 0x00:2.4
   * - Byte 1: blMode — 当前灯效模式
   * - Byte 2: blLevel —  当前灯效亮度
   * - Byte 3: blSpeed — 当前灯效速度
   * - Byte 4: blColor — 当前灯效颜色类型
   * - Byte 5: enterSleepTime — 进入睡眠的时间
   * - Byte 6: RPT
   * - Byte 7: BAT — 电池电量百分比 0~100
   *
   * - Byte 8 - 9: FUC_WM配置参数的 u16 配置参数的功能组合
   *
   * 分别解析进行位运算？
   *    112 64
   *    116 64
   *    116 128
   *   0111 0000 0100 0000
   *   0111 0100 0100 0000
   *   0111 0100 1000 0000
   *   7654 3210
   *   FUC_WM：
   *        Bit0:sys_mode 系统模式 0:win 1:mac
   *        Bit1:fn_lock
   *        Bit2:win_locl
   *        Bit3:full_locl:全检无冲使能
   *        Bit4:bl_en  :背光使能
   *        Bit5:display_en:显示屏使能
   *        Bit6: connect_state
   *        Bit7: save
   *        Bit8: low_bat
   *        Bit9: pair_state
   *        Bit10~`11: signal_quality
   *        Bit12~`13: scan_type
   *        Bit14~`15: bat_state  0x02:usb 0x01:ble/2.4 电量状态（0=无效, 1=有效, 2=充电中）
   *
   * - Byte 10 - 11: USB_PID  同Byte 8 -9 的解析方式
   *  20 72
   *  0001 0100 0100 1000
   *
   * - Byte 12 RF_TYPE
   * - Byte 13 RSSI
   * - Byte 14 CUSTOM1  A83AIR：0状态岛灯光关闭 1灯光开启
   *
   * @returns 解析后的 telinkDeviceCustom 对象
   */
  private parseUserCustomData(buffer: Uint8Array): telinkDeviceCustom {
    // byte0: cfgSel(高4位) + connectType(低4位 0:2.4G 1:BLE 2:USB)
    const cfgSel = (buffer[0] >> 4) & 0x0f
    const connectType = (buffer[0] & 0x0f) as 0 | 1 | 2

    // 基础字段(byte 0~7)
    const base = {
      cfgSel,
      blMode: buffer[1],
      blLevel: buffer[2],
      blSpeed: buffer[3],
      blColor: buffer[4],
      enterSleepTime: buffer[5] as number | undefined,
      rpt: buffer[6] as 0x01 | 0x02 | 0x04 | 0x08 | 0x81 | 0x82 | 0x84 | undefined,
      // 按新协议注释:byte 6 = RPT、byte 7 = BAT(电池百分比)。
      // 旧实现把 buffer[6] 当 batPercent 是 byte 错位,这里修正。
      batPercent: buffer[7],
      // batStatus 字段在 base 中先占位(buffer < 15 时走默认值 0=无效),FUC_WM 解析后会被覆盖。
      batStatus: 0 as 0 | 1 | 2,
      connectType,
    } as telinkDeviceCustom

    // 扩展字段(byte 8~14)需要 buffer 至少 15 字节,长度不足时跳过 FUC_WM/USB_PID/RF_TYPE/RSSI/CUSTOM1。
    if (buffer.length < 15) {
      return base
    }

    // byte8-9: FUC_WM u16 小端序(配置参数的功能组合)
    const fucWm16 = (buffer[9] << 8) | buffer[8]
    const sysMode = (fucWm16 & 0x0001) as 0 | 1 // Bit0: 0=win 1=mac
    const fnLock = ((fucWm16 >> 1) & 0x0001) as 0 | 1 // Bit1
    const winLock = ((fucWm16 >> 2) & 0x0001) as 0 | 1 // Bit2
    const fullKeyLock = ((fucWm16 >> 3) & 0x0001) as 0 | 1 // Bit3: 全键无冲
    const blOnOff = ((fucWm16 >> 4) & 0x0001) as 0 | 1 // Bit4: 背光使能
    const disOnOff = ((fucWm16 >> 5) & 0x0001) as 0 | 1 // Bit5: 显示屏使能
    const connectState = ((fucWm16 >> 6) & 0x0001) as 0 | 1 // Bit6
    const save = ((fucWm16 >> 7) & 0x0001) as 0 | 1 // Bit7
    const loBat = ((fucWm16 >> 8) & 0x0001) as 0 | 1 // Bit8: 低电
    const pairFlag = ((fucWm16 >> 9) & 0x0001) as 0 | 1 // Bit9: 配对状态
    const rfQuality = ((fucWm16 >> 10) & 0x0003) as 0 | 1 | 2 | 3 // Bit10~11: 信号质量
    const scanType = ((fucWm16 >> 12) & 0x0003) as 0 | 1 | 2 // Bit12~13: 扫描类型
    const batStatus = ((fucWm16 >> 14) & 0x0003) as 0 | 1 | 2 // Bit14~15: 电量状态 0=无效,1=有效,2=充电中

    // byte10-11: USB_PID u16 小端序
    const usbPid = (buffer[11] << 8) | buffer[10]

    return {
      ...base,
      sysMode,
      fnLock,
      winLock,
      fullKeyLock,
      blOnOff,
      disOnOff,
      connectState,
      save,
      loBat,
      pairFlag,
      rfQuality,
      scanType,
      batStatus,
      usbPid,
      rfType: buffer[12],
      rssi: buffer[13],
      custom1: buffer[14],
    }
  }

  /**
   * V700的协议报告需要单独处理
   * 解析 0x01 用户自定义数据（7 字节，Telink 键盘专用格式）
   *
   * 字节布局：
   * - Byte 0: cfgSel — 当前配置模式
   * - Byte 1: blMode — 当前灯效模式
   * - Byte 2: blSpeed — 当前灯效速度
   * - Byte 3: blLevel — 当前灯效亮度
   * - Byte 4: blColor — 当前灯效颜色类型
   * - Byte 5: batStatus — 电量状态（0=无效, 1=有效, 2=充电中）
   * - Byte 6: batPercent — 电池电量百分比 0~100
   *
   * @returns 解析后的 telinkDeviceCustom 对象
   */
  private parseV700UserCustomData(buffer: Uint8Array): telinkDeviceCustom {
    return {
      cfgSel: buffer[0],
      blMode: buffer[1],
      blSpeed: buffer[2],
      blLevel: buffer[3],
      blColor: buffer[4],
      batStatus: (buffer[5] > 2 ? 0 : buffer[5]) as 0 | 1 | 2,
      batPercent: buffer[6],
    }
  }
}
